import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { DollarSign, Building2, Home, Users } from 'lucide-react';
import { calculatePricing, formatPrice, PRICING_VERSION } from '../utils/pricingEngine';

const PricingCalculator = ({ accountType = 'landlord', onGetStarted }) => {
  const [units, setUnits] = useState(5);
  const [buildings, setBuildings] = useState(1);
  const [pricing, setPricing] = useState({
    setupFee: 0,
    monthlyFee: 0,
    transactionFee: 5
  });

  useEffect(() => {
    // Use single source of truth pricing engine
    const calculatedPricing = calculatePricing(accountType, units, buildings);
    setPricing(calculatedPricing);
  }, [units, buildings, accountType]);

  const handleUnitsChange = (value) => {
    setUnits(value[0]);
  };

  const handleBuildingsChange = (value) => {
    setBuildings(value[0]);
  };

  const getAccountTypeLabel = () => {
    switch (accountType) {
      case 'landlord':
        return 'Landlord';
      case 'hoa':
        return 'HOA Community';
      case 'property_manager':
        return 'Property Manager';
      case 'vacation_rental':
        return 'Vacation Rentals';
      default:
        return 'Account';
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center text-2xl">
          <DollarSign className="h-6 w-6 mr-2 text-emerald-600" />
          Calculate Your Pricing
        </CardTitle>
        <p className="text-gray-600 mt-2">
          Adjust the sliders to see your custom pricing for {getAccountTypeLabel()}
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Units Slider */}
        {accountType !== 'property_manager' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Home className="h-5 w-5 text-gray-600" />
                <Label className="text-base font-medium">
                  {accountType === 'vacation_rental' ? 'Number of Properties' : 'Number of Units'}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-emerald-600">
                  {accountType === 'vacation_rental' && units > 50 ? '51+' : units}
                </span>
                <span className="text-gray-500">{accountType === 'vacation_rental' ? 'properties' : 'units'}</span>
              </div>
            </div>
            <Slider
              value={[units]}
              onValueChange={handleUnitsChange}
              min={1}
              max={accountType === 'landlord' ? 100 : accountType === 'vacation_rental' ? 60 : 200}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>1 {accountType === 'vacation_rental' ? 'property' : 'unit'}</span>
              <span>{accountType === 'landlord' ? '100' : accountType === 'vacation_rental' ? '51+' : '200'} {accountType === 'vacation_rental' ? 'properties' : 'units'}</span>
            </div>
          </div>
        )}

        {/* Buildings Slider (Landlord & HOA only - not for Vacation Rental) */}
        {(accountType === 'landlord' || accountType === 'hoa') && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5 text-gray-600" />
                <Label className="text-base font-medium">Number of Buildings</Label>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-emerald-600">{buildings}</span>
                <span className="text-gray-500">buildings</span>
              </div>
            </div>
            <Slider
              value={[buildings]}
              onValueChange={handleBuildingsChange}
              min={1}
              max={20}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>1 building</span>
              <span>20 buildings</span>
            </div>
          </div>
        )}

        {/* Pricing Summary */}
        <div className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-lg p-6 space-y-4 border-2 border-emerald-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Pricing Summary</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-700">One-time Setup Fee</span>
              <span className="text-2xl font-bold text-emerald-600">
                ${pricing.setupFee.toLocaleString()}
              </span>
            </div>
            
            <div className="border-t border-emerald-200 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Monthly Subscription</span>
                <span className="text-2xl font-bold text-emerald-600">
                  ${pricing.monthlyFee.toLocaleString()}/mo
                </span>
              </div>
              {accountType === 'landlord' && (
                <p className="text-xs text-gray-500 mt-1">
                  For {units} units across {buildings} building(s)
                  {buildings > 1 && ` (+$${(buildings - 1) * 54}/mo for multi-location)`}
                </p>
              )}
              {accountType === 'hoa' && (
                <p className="text-xs text-gray-500 mt-1">
                  For {units} units across {buildings} building(s)
                  {buildings > 1 && ` (+$${(buildings - 1) * 65}/mo for multi-building)`}
                </p>
              )}
              {accountType === 'vacation_rental' && (
                <p className="text-xs text-gray-500 mt-1">
                  For {units > 50 ? '51+' : units} properties
                  {units > 50 && ' - Contact sales for custom pricing'}
                </p>
              )}
            </div>
            
            <div className="border-t border-emerald-200 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Transaction Fee</span>
                <span className="text-lg font-semibold text-gray-900">
                  {pricing.transactionFee}% per payment
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded p-3 mt-4 border border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">First Month Total</span>
              <span className="text-xl font-bold text-gray-900">
                ${(pricing.setupFee + pricing.monthlyFee).toLocaleString()}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Setup fee + first month subscription
            </p>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-3">What's Included:</h4>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Complete property management platform</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>{accountType === 'hoa' ? 'Home Owner portal with payment processing' : 'Tenant portal with payment processing'}</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Financial reporting and analytics</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>Maintenance request management</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">✓</span>
              <span>24/7 customer support</span>
            </li>
          </ul>
        </div>

        {/* CTA Button */}
        <Button
          onClick={() => onGetStarted && onGetStarted(accountType, units, buildings, pricing)}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
          size="lg"
        >
          Get Started with {getAccountTypeLabel()} Plan
        </Button>

        <p className="text-xs text-center text-gray-500">
          Professional property management made simple • Cancel anytime
        </p>
      </CardContent>
    </Card>
  );
};

// Simple Label component if not imported
const Label = ({ children, className = '' }) => (
  <label className={`text-sm font-medium text-gray-700 ${className}`}>
    {children}
  </label>
);

export default PricingCalculator;
